
import { Items, VehicleTypes } from "@/api";
import { Item } from "@/api/items";
import { VehicleType } from "@/api/vehicleTypes";
import PageTitle from "@/components/common/PageTitle.vue";
import VCurrencyField from "@/components/forms/VCurrencyField.vue";
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";

const Auth = namespace("auth");

const emptyItem = (): Item => ({
  name: "",
  hansaCode: "",
  supplier: "",
  vehicleTypeIds: [],
  price: 0,
  qtyOnHand: 0
});

@Component({ components: { PageTitle, VCurrencyField } })
export default class ItemMasterPage extends Vue {
  @Auth.Getter("getUser") getUser!: any;

  pageHeaders = [{ icon: "mdi-package-variant-closed", header: "Item Master" }];
  headers = [
    { text: "Item Name", value: "name", align: "start" },
    { text: "Hansa Code", value: "hansaCode", align: "start" },
    { text: "Supplier", value: "supplier", align: "start" },
    { text: "Vehicle Type", value: "vehicleTypes", align: "start" },
    { text: "Price", value: "price", align: "start", width: "120px" },
    { text: "Qty on Hand", value: "qtyOnHand", align: "start", width: "120px" },
    {
      text: "Actions",
      value: "actions",
      align: "start",
      sortable: false,
      width: "150px"
    }
  ];

  items: Item[] = [];
  vehicleTypes: VehicleType[] = [];
  loading = false;
  saving = false;
  showDialog = false;
  editingId: number | null = null;
  form: Item = emptyItem();
  errors: Record<string, string[]> = {};
  filters: { search: string | null; vehicleTypeId: number | null } = {
    search: null,
    vehicleTypeId: null
  };

  private searchTimer: number | null = null;

  get permissions(): string[] {
    return (this.getUser && this.getUser.permissions) || [];
  }
  get canView(): boolean {
    return this.permissions.includes("view_item_master");
  }
  get canCreate(): boolean {
    return this.permissions.includes("create_item_master");
  }
  get canEdit(): boolean {
    return this.permissions.includes("edit_item_master");
  }
  get canDelete(): boolean {
    return this.permissions.includes("delete_item_master");
  }

  async created(): Promise<void> {
    if (!this.canView) return;
    await Promise.all([this.loadItems(), this.loadVehicleTypes()]);
  }

  // Typing in the search box should not fire a request per keystroke.
  @Watch("filters.search")
  onSearchChange(): void {
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => this.loadItems(), 350);
  }

  @Watch("filters.vehicleTypeId")
  async onVehicleTypeChange(): Promise<void> {
    await this.loadItems();
  }

  async loadItems(): Promise<void> {
    this.loading = true;
    try {
      this.items = await Items.list({
        search: this.filters.search || null,
        vehicleTypeId: this.filters.vehicleTypeId || null
      });
    } catch (e) {
      await this.$error(e);
    } finally {
      this.loading = false;
    }
  }

  async loadVehicleTypes(): Promise<void> {
    try {
      this.vehicleTypes = await VehicleTypes.list();
    } catch (e) {
      this.vehicleTypes = [];
    }
  }

  openCreate(): void {
    this.editingId = null;
    // Every type by default: most parts fit both, and it is easier to untick
    // one than to remember to tick both.
    this.form = {
      ...emptyItem(),
      vehicleTypeIds: this.vehicleTypes.map(type => type.id)
    };
    this.errors = {};
    this.showDialog = true;
  }

  openEdit(item: Item): void {
    this.editingId = item.id || null;
    this.form = { ...item };
    this.errors = {};
    this.showDialog = true;
  }

  closeDialog(): void {
    this.showDialog = false;
    this.errors = {};
  }

  async save(): Promise<void> {
    this.saving = true;
    this.errors = {};
    try {
      const payload: Item = {
        ...this.form,
        price: Number(this.form.price || 0),
        qtyOnHand: Number(this.form.qtyOnHand || 0)
      };
      if (this.editingId) {
        await Items.update(this.editingId, payload);
      } else {
        await Items.create(payload);
      }
      this.showDialog = false;
      await this.loadItems();
      await this.$success("<strong>Success!</strong> Item saved.");
    } catch (e) {
      this.errors = this.fieldErrors(e);
      if (!Object.keys(this.errors).length) await this.$error(e);
    } finally {
      this.saving = false;
    }
  }

  async confirmDelete(item: Item): Promise<void> {
    const answer = await this.$dialog.error({
      text: `Delete "${item.name}"?`,
      title: "Warning",
      persistent: false,
      actions: { No: { text: "No" }, Yes: { color: "red", text: "Yes I do" } }
    });
    if (answer !== "Yes" || !item.id) return;
    try {
      await Items.remove(item.id);
      await this.loadItems();
      await this.$success("<strong>Success!</strong> Item deleted.");
    } catch (e) {
      await this.$error(e);
    }
  }

  // Laravel returns 422 with an errors bag keyed by the snake_case field.
  fieldErrors(error: any): Record<string, string[]> {
    const bag = error?.response?.data?.errors || error?.errors;
    if (!bag) return {};
    // snake_case from the API to the camelCase the form binds to
    const toCamelKey = (key: string): string =>
      key.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase());

    return Object.keys(bag).reduce((carry: Record<string, string[]>, key) => {
      carry[toCamelKey(key)] = bag[key];
      return carry;
    }, {});
  }

  formatMoney(value: number): string {
    return `R ${Number(value || 0)
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}`;
  }
}
